/**
 * US phone normalization, validation and display formatting.
 *
 * Storage canon is E.164 (`+18162843000`); the display canon is
 * `(816) 284-3000`. Every write boundary lands phones on that canon
 * regardless of source (typed admin/portal inputs, the masked consultation
 * form, CSV imports, lead copies) — the import matcher compares phones as
 * exact strings, so format variance silently kills that dedupe signal.
 * Render sites call `formatPhoneDisplay`.
 *
 * TWO-TIER WRITE CONTRACT (owner amendment 2026-09-03, superseding the
 * "unparseables stay raw" half of the 8/25 item-11 rule):
 *
 *   STRICT tier — USER INPUT. A human typing into a form goes through
 *   `validatePhoneInput`, which REJECTS anything that isn't a US phone so
 *   the route can answer with an inline 400 carrying
 *   PHONE_REQUIREMENT_MESSAGE. This closes the reported bug where an
 *   under-length entry (7 digits) in the builder detail editor was stored
 *   and redisplayed as the malformed value the user typed.
 *
 *   LENIENT tier — MACHINE / LEGACY paths. Copies of already-stored rows
 *   (lead → company registration, CRM sync payloads) and CSV/staged import
 *   data keep using `normalizePhoneForWrite`: there is no human at the
 *   keyboard to correct, and refusing would drop data we already hold. The
 *   worker's importer already returns null for an unparseable phone and
 *   soft-flags the row, so malformed values barely reach this tier.
 *
 * US-only either way: exactly 10 digits, or 11 with a leading country-code
 * 1, in any punctuation. HighLevel receives what we store — normalization
 * happens in the route BEFORE insert + CRM sync, and stored rows are never
 * backfill-pushed to HL. Pre-existing malformed stored rows are left alone
 * (no migration); `formatPhoneDisplay` still renders them as stored.
 *
 * WHY a REFUSAL IS SPECIFIC (owner ruling 2026-09-03, follow-on): a single
 * generic sentence made the user guess which of several mistakes they made,
 * so `classifyPhoneInput` names the class — too few digits, too many, an
 * extension, not a US number, no digits at all — and `validatePhoneInput`
 * returns that message. Every strict boundary inherits the better copy for
 * free.
 *
 * The classifier is also the AUTHORITY on acceptability at the strict tier
 * (`validatePhoneInput` consults it before parsing), and its extension class
 * deliberately NARROWS what the strict tier accepts: a value carrying an
 * extension marker is refused even when its digits would parse, because
 * "816-284-300 ext 0" is ten digits and storing it would invent the last
 * one. The LENIENT tier is untouched — it never calls the classifier.
 *
 * The copy is written to inform, not to scold (owner refinement, same day):
 * an incomplete number opens "Almost there", and the counts stay because the
 * count is the useful part.
 *
 * NEVER INVENT A DIGIT (owner ruling, same day, superseding an intermediate
 * clamp-the-mask ruling): `formatPhoneAsTyped` — the input mask every form
 * shares — formats but never DISCARDS. Clamping to ten digits had turned a
 * pasted "+33 1 42 68 53 00" into "(331) 426-8530" and accepted it as
 * +13314268530, which is the same fabrication as accepting
 * "816-284-300 ext 0". Past a US shape the mask now hands back bare digits
 * and lets the classifier refuse them; the extension carve-out keeps a
 * pasted marker visible for the same reason. See `formatPhoneAsTyped`.
 *
 * MUST stay in sync with the worker's copy
 * (services/worker/src/row-parser.ts `formatPhone`) — the importer writes
 * staging rows with the same canon. A lockstep test in
 * src/lib/__tests__/phone.test.ts imports both and pins the parity.
 */

/**
 * The standing REQUIREMENT sentence: what a phone has to be, stated without
 * reference to what the user typed. It is the hint copy (see
 * PHONE_HINT_TEXT for the short form that fits under an input) and the
 * fallback message when no specific problem class applies.
 *
 * Says "10 digits", not "at least 10": the too-many-digits refusal below
 * quotes the same rule, and the two must not contradict each other.
 */
export const PHONE_REQUIREMENT_MESSAGE =
  'Enter a US phone number: 10 digits starting with the area code — the +1 country code is added automatically.';

/**
 * Short form of the requirement, for helper text under a phone input where
 * the placeholder already shows the shape. Shared so the public, admin and
 * portal phone fields that carry it can't drift apart.
 */
export const PHONE_HINT_TEXT = '10 digits, starting with the area code — +1 is added automatically.';

/**
 * The one example number every phone input shows (owner ruling 2026-09-03:
 * the public and admin/portal forms had drifted onto different examples).
 * Exported so a form can't invent its own again.
 *
 * MUST keep 555 in the area-code position and MUST NOT be dialable: 555 is
 * not an assignable area code, which is what makes 555 numbers the
 * recognized fictional-example convention. The assessment form used to show
 * (816) 284-3000 — a live number in our own area code, so a user could dial
 * the placeholder. A test pins the 555 for that reason. Deliberately shaped
 * like `formatPhoneAsTyped`'s output so the example and the mask agree.
 */
export const PHONE_PLACEHOLDER = '(555) 123-4567';

/**
 * The extension sentence, shared by the classifier's refusal and the forms'
 * muted notice so the two cannot drift. Phrased as what to do, not as a
 * claim about what we kept — the mask keeps the whole pasted value.
 */
export const PHONE_EXTENSION_MESSAGE =
  "We don't support phone extensions yet — enter just the 10-digit number.";

/**
 * Digits of a phone-ish string, with Excel's scientific-notation mangling
 * ("1.8162843e10") expanded first for parity with the worker's importer.
 * Parsing and classification share it, so a value can never be accepted by
 * one and miscounted by the other. `formatPhoneAsTyped` deliberately does
 * NOT expand: mid-typing, "1.8" is two digits the user can still finish,
 * not the number 2.
 */
function phoneDigits(value: string): string {
  let cleaned = value.trim();
  if (/^\d+\.?\d*e?\+?\d*$/i.test(cleaned)) {
    const num = parseFloat(cleaned);
    if (!isNaN(num)) {
      cleaned = Math.round(num).toString();
    }
  }
  return cleaned.replace(/\D/g, '');
}

/**
 * Parse a US phone into E.164, or null when it isn't one.
 *
 * Accepts 10 digits, or 11 digits with a leading country-code 1, in any
 * punctuation ("(816) 284-3000", "816.284.3000", "+1 816 284 3000", …).
 * Also tolerates Excel's scientific-notation mangling ("1.8162843e10") for
 * parity with the worker's importer.
 */
export function parsePhoneUsE164(value: string | null | undefined): string | null {
  if (!value) return null;

  const digits = phoneDigits(String(value));
  if (digits.length === 11 && digits.startsWith('1')) {
    return `+${digits}`;
  }
  if (digits.length === 10) {
    return `+1${digits}`;
  }
  return null;
}

/**
 * Why a typed phone was refused. Mirrors the five mistakes a human actually
 * makes; anything else is accepted, so there is no "other" member.
 */
export type PhoneInputProblem =
  | 'no_digits'
  | 'too_short'
  | 'not_us'
  | 'too_long'
  | 'has_extension';

/**
 * An extension marker followed by its digits at the END of the value:
 * "ext", "ext.", "extension" or a bare "x", any case, any punctuation or
 * spacing between marker and digits ("ext. 4", "x12", "EXTENSION 7",
 * "x-4").
 *
 * Digits are REQUIRED after the marker so a trailing stray letter ("816-284-
 * 3000 x") isn't read as an extension — that value's 10 digits are a fine
 * phone and the empty marker carries no information.
 *
 * WHY a bare "x" counts: it is the most common way people write an
 * extension ("8162843000x12"), and a real US phone never contains an x, so
 * the letter itself is unambiguous. The residual false-positive is a word
 * ENDING in x followed by an unpunctuated digit run — "Fax 8162843000" —
 * which is why the marker must be preceded by a non-letter or start the
 * value. (Written without a lookbehind on purpose: this module runs in the
 * browser, and lookbehind is a parse-time SyntaxError on Safari below 16.4,
 * which would take the whole page down rather than misclassify one value.)
 */
const PHONE_EXTENSION_SUFFIX = /(?:^|[^A-Za-z])(?:ext(?:ension)?\.?|x)[\s.:#-]*\d+\s*$/i;

/**
 * Name the problem with a typed phone, or null when the value is fine
 * (a US number, or empty — phone is optional everywhere it's collected).
 *
 * The message is the user-facing correction instruction; it quotes the digit
 * count back so the count and the field agree — which is exactly why
 * `formatPhoneAsTyped` refuses to swallow digits past the mask's shape.
 *
 * 11 digits not starting with 1 is reported as international rather than
 * "one digit too many": we cannot tell a Paris number from a fat-fingered
 * local one, and only the international reading explains why we refuse a
 * number that is otherwise well-formed.
 *
 * The extension check runs FIRST — above the two accept branches, not just
 * above the count-based classes. Extension digits inflate the count, which
 * is why "816-284-3000 ext. 4" used to be explained as an international
 * number; worse, a 9-digit base plus a 1-digit extension
 * ("816-284-300 ext 0") totals ten digits, so the accept branch took it and
 * the write boundary stored +18162843000 — a last digit the user never
 * typed, invented out of an extension. We don't support extensions, so a
 * value carrying one is not acceptable in ANY form, however its digits
 * happen to total.
 */
export function classifyPhoneInput(
  value: unknown,
): { problem: PhoneInputProblem; message: string } | null {
  if (value == null) return null;
  const trimmed = String(value).trim();
  if (trimmed === '') return null;

  if (PHONE_EXTENSION_SUFFIX.test(trimmed)) {
    return { problem: 'has_extension', message: PHONE_EXTENSION_MESSAGE };
  }

  const digits = phoneDigits(trimmed);
  if (digits.length === 10) return null;
  if (digits.length === 11 && digits.startsWith('1')) return null;

  if (digits.length === 0) {
    return { problem: 'no_digits', message: 'Enter a phone number using digits.' };
  }
  if (digits.length < 10) {
    // "1 of the 10 digits" is already grammatical, so the count needs no
    // singular/plural branch.
    return {
      problem: 'too_short',
      message: `Almost there — that's ${digits.length} of the 10 digits we need. Please provide an area code.`,
    };
  }
  if (digits.length === 11) {
    return {
      problem: 'not_us',
      message:
        'That looks like an international number — we can only accept US numbers right now.',
    };
  }
  return {
    problem: 'too_long',
    message: `That's ${digits.length} digits — more than we need. A US number has 10, or 11 with a leading 1.`,
  };
}

/**
 * STRICT tier: validate a phone a user just typed.
 *
 * Empty (null / undefined / whitespace-only) is fine — phone is optional
 * everywhere it's collected — and yields `{ ok: true, value: null }` so the
 * caller can clear the field. A parseable US number yields E.164. Anything
 * else is REJECTED — under 10 digits, 11 digits not starting with 1, 12+
 * digits, a trailing extension (we cannot store one), letters-only text —
 * with the SPECIFIC message for that class (see
 * `classifyPhoneInput`) for the route to return as an inline 400. The
 * problem class rides along so a caller can branch on it without matching
 * copy; PHONE_REQUIREMENT_MESSAGE is the fallback if a value somehow fails
 * to parse without landing in a class.
 *
 * The CLASSIFIER IS THE AUTHORITY here, consulted before the parser: asking
 * `parsePhoneUsE164` first would re-admit values the classifier refuses,
 * because a value carrying an extension can still total ten digits
 * ("816-284-300 ext 0") and parse cleanly into a number nobody typed. One
 * source of truth for "is this acceptable" instead of two branches that can
 * disagree.
 *
 * DELIBERATE NARROWING of the accepted set (owner ruling 2026-09-03,
 * superseding the earlier "the accepted set does not change" instruction on
 * exactly this point): the strict tier now refuses anything carrying an
 * extension marker EVEN WHEN its digits would otherwise parse, because
 * accepting it means inventing a digit. Nothing else about the accepted set
 * moved.
 *
 * Callers that are copying stored/machine data rather than reading a form
 * want `normalizePhoneForWrite` instead — see the two-tier note above. That
 * tier is UNCHANGED by this narrowing: it goes through `parsePhoneUsE164`,
 * never this function, so CSV import promotion and copies of stored rows
 * behave exactly as before.
 */
export function validatePhoneInput(
  value: unknown,
):
  | { ok: true; value: string | null }
  | { ok: false; problem: PhoneInputProblem; message: string } {
  if (value == null) return { ok: true, value: null };
  const trimmed = String(value).trim();
  if (trimmed === '') return { ok: true, value: null };

  const problem = classifyPhoneInput(trimmed);
  if (problem) {
    return { ok: false, problem: problem.problem, message: problem.message };
  }

  const e164 = parsePhoneUsE164(trimmed);
  if (!e164) {
    // Unreachable in practice — the classifier and the parser share
    // `phoneDigits`, so silence there means 10 digits (or 11 with a leading
    // 1) here. Kept as a fail-closed guard rather than a non-null assertion.
    return { ok: false, problem: 'no_digits', message: PHONE_REQUIREMENT_MESSAGE };
  }
  return { ok: true, value: e164 };
}

/**
 * Progressive input mask for a phone the user is TYPING: digits only,
 * grouped as they arrive ("816" → "(816) 284" → "(816) 284-3000", and
 * "1" + 10 more → "+1 (816) 284-3000"). Extracted from ConsultationModal so
 * every form masks identically.
 *
 * FORMATS BUT NEVER DISCARDS A DIGIT (owner ruling 2026-09-03, superseding
 * the clamp-at-a-US-length ruling of the same day). The rule, in order:
 *
 *   1. No digits at all → the value passes through untouched, so letters or
 *      stray punctuation stay visible for the "use digits" refusal instead
 *      of vanishing as the user types.
 *   2. A trailing EXTENSION marker with digits → untouched as well. See the
 *      carve-out note below; this is the one case where masking would
 *      manufacture a number even inside a US length.
 *   3. 10 digits or fewer → progressive mask ("816" → "(816) 284" →
 *      "(816) 284-3000").
 *   4. Exactly 11 digits starting with the country-code 1 →
 *      "+1 (816) 284-3000".
 *   5. Anything past that — 11 digits not starting with 1, or 12+ digits
 *      whatever they start with → the bare digits, UNFORMATTED. The mask
 *      visibly breaks, and that break is the signal: this is not a US
 *      number. Blur/submit then refuses it honestly (`not_us` / `too_long`).
 *
 * WHY CLAMPING BY DISCARD WAS REJECTED: slicing to 10 digits turned a
 * pasted foreign number into a plausible, wrong US one. "+33 1 42 68 53 00"
 * became "(331) 426-8530" and was ACCEPTED as +13314268530 — a number
 * belonging to someone else entirely, stored without a word to the user.
 * "28162843000" (a non-US 11-digit) became "(281) 628-4300" the same way.
 * Inventing digits is the worse failure: a visible, correctable broken state
 * beats a silent rewrite.
 *
 * CONSEQUENCE ACCEPTED DELIBERATELY: typing an 11th digit onto a complete US
 * number no longer vanishes — the field drops to unformatted digits and the
 * user is told "that looks like an international number" on blur. Deleting
 * back down re-masks, so the state is reversible.
 *
 * EXTENSION CARVE-OUT (rule 2): stripping "ext" while keeping its digits is
 * the same fabrication in miniature. "816-284-300 ext 0" is a 9-digit base
 * plus a 1-digit extension — ten digits in total — so masking it produces
 * "(816) 284-3000", which validates and stores +18162843000: a last digit
 * nobody typed. Leaving the pasted value alone keeps the marker where
 * `classifyPhoneInput` can see it, so the boundary answers `has_extension`
 * instead of accepting a manufactured number. A marker can only ever arrive
 * by paste or autofill: a letter typed into a masked field is stripped on
 * that keystroke, before any digits could follow it.
 *
 * The classifier stays lenient in DETECTION on purpose (12+ digits,
 * extensions, international shapes): direct API callers, CSV imports and the
 * unmasked file-imports editor never pass through this mask, so those
 * classes are live, not dead code.
 *
 * KNOWN LIMITATION (controlled input, no caret management): deleting a
 * DIGIT always shortens the value, so backspace never stalls. Deleting a
 * mask CHARACTER mid-string ("(816) 2843000" after killing the hyphen) has
 * the same digits, so it re-masks to the identical string and the keypress
 * looks ignored; the caret may also land at the end of the field after an
 * edit made in the middle. Fixing either needs per-form keydown/selection
 * handling, which is not worth the weight here.
 */
export function formatPhoneAsTyped(value: string): string {
  const raw = String(value ?? '');
  const digits = raw.replace(/\D/g, '');

  if (digits.length === 0) return raw;
  if (PHONE_EXTENSION_SUFFIX.test(raw.trim())) return raw;

  if (digits.length <= 3) return digits;
  if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  if (digits.length <= 10) {
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  }
  if (digits.length === 11 && digits.startsWith('1')) {
    return `+1 (${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7)}`;
  }
  // Past a US shape: bare digits, nothing dropped. The broken mask is the
  // signal, and the refusal on blur says which US rule the value breaks.
  return digits;
}

/**
 * The one thing worth saying about a value the mask deliberately left
 * unformatted: it carries an extension, which we can't store.
 *
 * NOT error copy — it renders muted, never blocks submit, and is recomputed
 * (so cleared) on the next edit. It fires the moment the paste lands, ahead
 * of the blur-time refusal, because an extension is the one problem a user
 * can't diagnose from a field that simply stopped formatting.
 *
 * There is no "we dropped some digits" companion: under the mask's
 * never-discard rule there are no dropped digits to report.
 */
export function phoneMaskNotice(value: string | null | undefined): string | null {
  const raw = String(value ?? '');
  if (raw.replace(/\D/g, '').length === 0) return null;
  return PHONE_EXTENSION_SUFFIX.test(raw.trim()) ? PHONE_EXTENSION_MESSAGE : null;
}

/**
 * LENIENT tier: what a machine/legacy write boundary stores — E.164 when
 * the value parses as a US phone, the trimmed original when it doesn't, and
 * null for empty input.
 *
 * As of the 9/3 amendment this is NOT the normalizer for user input (that
 * is `validatePhoneInput`, which rejects instead of degrading). It survives
 * for the paths with no human to correct: copies of already-stored rows
 * (assessment/consultation → register-company, CRM sync payloads) and
 * CSV/staged import promotion. The 20-character clamp therefore only
 * matters on this tier — every phone column is varchar(20), and an
 * oversized legacy value must degrade to a truncated string rather than a
 * 22001 database error.
 */
export function normalizePhoneForWrite(value: string | null | undefined): string | null {
  if (value == null) return null;
  const trimmed = String(value).trim();
  if (trimmed === '') return null;
  return parsePhoneUsE164(trimmed) ?? trimmed.slice(0, 20);
}

/**
 * Display form: `(816) 284-3000` for anything that parses as a US phone
 * (E.164 from storage, or legacy un-backfilled shapes), the value as-is
 * when it doesn't, and '' for empty — callers keep their own '—' fallback.
 */
export function formatPhoneDisplay(value: string | null | undefined): string {
  if (!value) return '';
  const e164 = parsePhoneUsE164(value);
  if (!e164) return String(value);
  const ten = e164.slice(2);
  return `(${ten.slice(0, 3)}) ${ten.slice(3, 6)}-${ten.slice(6)}`;
}
